"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import getURLParameter from "src/lib/client/getURLParameter";
import SearchParamsChangeListener from "src/lib/client/SearchParamsChangeListener";
import { useLocationContext, useSelectedCinemaContext } from "src/lib/contexts/ContextTypes";
import isCookieBotEnabled from "src/lib/isCookiebotEnabled";
import { AdContext } from "src/lib/types/ads/AdContextType";
import type { AdUnit } from "src/lib/types/ads/AdUnit";
import type { KV } from "src/lib/types/ads/KV";
import { type ICommonAppData } from "src/lib/types/ICommonAppData";
const BRAND = process.env.NEXT_PUBLIC_WEBSITE_BRAND;

//#region [Props]
type LOADING_IMMEDIATE = "immediate";
type LOADING_CONTENT = "content";
type AdWrapperProps = {
  kv?: KV[] | null;
  children: React.ReactNode;
  ads: AdUnit[];
  defaultProximity?: number;
  appData: ICommonAppData;
};
//#endregion

export default function AdWrapper(t0) {
  const $ = _c(12);
  const {
    kv,
    children,
    ads,
    defaultProximity: t1,
    appData
  } = t0;
  const defaultProximity = t1 === undefined ? 0 : t1;
  if (BRAND === "kinodk") {
    let t2;
    if ($[0] !== ads || $[1] !== appData || $[2] !== children || $[3] !== defaultProximity || $[4] !== kv) {
      t2 = <KinoDkAdWrapper ads={ads} appData={appData} kv={kv} defaultProximity={defaultProximity}>{children}</KinoDkAdWrapper>;
      $[0] = ads;
      $[1] = appData;
      $[2] = children;
      $[3] = defaultProximity;
      $[4] = kv;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t2;
  if ($[6] !== ads || $[7] !== appData || $[8] !== children || $[9] !== defaultProximity || $[10] !== kv) {
    t2 = <FilmwebAdWrapper ads={ads} appData={appData} kv={kv} defaultProximity={defaultProximity}>{children}</FilmwebAdWrapper>;
    $[6] = ads;
    $[7] = appData;
    $[8] = children;
    $[9] = defaultProximity;
    $[10] = kv;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}

//#region [Component]
function FilmwebAdWrapper({
  kv,
  children,
  ads,
  defaultProximity = 0,
  appData
}: AdWrapperProps) {
  const _getAuId = (targetId: string) => ads.find(ad => ad.targetId === targetId)?.auId ?? null;
  const locationContext = useLocationContext();
  const cinemaContext = useSelectedCinemaContext();
  const [_kv, setKv] = useState(kv);
  const [loadedAds, setLoadedAds] = useState<string[]>([]);
  const [noMatchedAds, setNotMatchedAds] = useState<string[]>([]);
  const hasRequestedAds = useRef(false);
  const [refreshTick, setRefreshTick] = useState(0);
  //const [_refreshTime, setRefreshTime] = useState(Date.now());
  //console.debug("Refresh time", _refreshTime);
  const pathname = usePathname();
  // no useSearchParams here: it would suspend the whole page while it is prerendered. Ads are only
  // requested once per page (hasRequestedAds), so a query string change doesn't need to trigger anything

  useEffect(() => {
    console.debug("%cClearing out", "color: white; background: indigo");
    window?.adn?.clearOut?.();
  }, [pathname]);
  useEffect(() => {
    if ((ads?.length ?? 0) > 0) {
      const skipAds = getURLParameter("skipAds");
      if (skipAds === "true") {
        return;
      }
      const __kv = _kv ? [..._kv] : [];
      const test = getURLParameter("test");
      if (test) {
        __kv.push({
          "test": [test as string]
        });
      }
      if (typeof window !== "undefined") {
        try {
          if (window.location.pathname) {
            __kv.push({
              "pathname": [window.location.pathname]
            });
          }
          if (window.location.search) {
            __kv.push({
              "search": [window.location.search]
            });
          }
        } catch (e) {
          console.error("Error setting KV");
        }
        console.debug("%cIs location ready?", "color: white; background: indigo;", locationContext.isReady);
        console.debug("%cIs cinema ready?", "color: white; background: indigo;", cinemaContext.isReady);
        console.debug("%cHas already requested ads?", "color: white; background: indigo;", hasRequestedAds.current);
        // add cinema location to the ad if the user has chosen a location
        if (locationContext.isReady !== false) {
          __kv.push({
            "location": locationContext.location ?? []
          });
        } else {
          console.debug("%cNot requesting ad because location is not initialized", "color: white; background: indigo");
          return;
        }

        // add cinema to the ad if the user is only showing a subset of available cinemas
        console.debug("CINEMACONTEXT", cinemaContext.isReady);
        if (cinemaContext.isReady !== false) {
          if (cinemaContext.selectedCinemas) {
            __kv.push({
              "cinema": cinemaContext.selectedCinemas
            });
          }
        } else {
          console.debug("%cNot requesting ad because selected cinema is not initialized", "color: white; background: indigo", hasRequestedAds.current);
          return;
        }
        if (hasRequestedAds.current) {
          console.debug("%cNot requesting ad because ads have already been requested", "color: white; background: indigo", hasRequestedAds.current);
          return;
        }
        const isDesktop = window.matchMedia(appData.adEnvironment!.desktopMq!).matches.toString();
        console.debug("%cIs DESKTOP?", "color: white; background: indigo;", isDesktop);
        __kv.push({
          "isdesktop": [isDesktop]
        });
        const adPayload = {
          useCookies: false,
          adUnits: ads.map(ad => {
            // merge individual ad units kv with "global" kv
            const _ad = {
              ...ad
            };
            if (_ad.kv) {
              _ad.kv = {
                ..._ad.kv,
                ...__kv
              };
            }
            if (!_ad.targetId) {
              // make sure we have a target Id
              _ad.targetId = `adn-${_ad.auId}`;
            }
            return _ad;
          }),
          kv: __kv,
          requestParams: {
            proximity: defaultProximity,
            load: "lazyRequest"
          },
          clearTarget: true,
          requestMode: "default",
          // default | hasTarget
          onPageLoad: function (adUnit: AdUnit) {
            console.debug("%cLoaded ad", "color: white; background: indigo;", adUnit);
            /*
            if (!loadedAds.includes(adUnit.targetId!)) {
                console.debug("Loaded ad", adUnit);
                //setLoadedAds((loaded) => [...loaded, adUnit.targetId]);
            }*/
          },
          onNoMatchedAds: function (adUnit: AdUnit) {
            console.debug("%cNo matched ads", "color: white; background: indigo;", adUnit); // arguments is language feature

            if (!noMatchedAds.includes(adUnit.targetId!)) {
              setNotMatchedAds([...noMatchedAds, adUnit.targetId!]);
            }
          } /*,
            onVisible: function( args: object ) {
            console.debug("On visible", args);
            },
            onViewable: function( args: object ) {
            console.debug("On Viewable", args);
            }*/
        };

        // make sure the adn object is defined
        if (!window.adn || !window.adn.calls) {
          //console.log("[ADCTX] pushing requestAds payload", adPayload);
          console.debug("%cReplacing adn", "color: white; background: indigo", window.adn, window.adn?.calls);
          window.adn = window.adn ?? {};
          window.adn.calls = window.adn.calls ?? [];
        }
        hasRequestedAds.current = true;
        window.adn.calls.push(() => {
          if (isCookieBotEnabled) {
            console.debug("%cMarketing consent status", "color: white; background: indigo", !!window?.Cookiebot?.consent?.marketing);
          } else {
            console.debug("%cMarketing consent status", "color: white; background: indigo", "Cookiebot not enabled for site");
          }
          adPayload.useCookies = isCookieBotEnabled && !!window?.Cookiebot?.consent?.marketing;
          window.adn.useLocalStorage(isCookieBotEnabled && !!window?.Cookiebot?.consent?.marketing);
          console.debug("%cRequesting ad", "color: white; background: indigo", adPayload, "KV", _kv, "Location", locationContext.location, "Selected cinemas", cinemaContext.selectedCinemas, "proximity", defaultProximity, "path", pathname);
          window.adn.requestAds(adPayload);
        });
      }
    }
    ;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads, _kv, locationContext.location, locationContext.isReady, cinemaContext.selectedCinemas, cinemaContext.isReady, /*_refreshTime,*/defaultProximity, pathname, refreshTick]);
  return <AdContext.Provider value={{
    loadedAds,
    noMatchedAds,
    kv: _kv,
    setKv,
    refresh: () => {
      //console.log("[ADCTX] refresh() called");
      hasRequestedAds.current = false;
      setRefreshTick(t => t + 1);
    },
    getAuId: _getAuId,
    registerAdUnit: targetId => {},
    unregisterAdUnit: targetId => {}
  }}>
        {children}
    </AdContext.Provider>;
}
//#endregion

//#region [Component]
let _conceptScriptLoaded = false;
function injectForTarget(registeredAds: Set<string>, adUnitId: string, targetId?: string | null) {
  const c = window?.concept;
  if (registeredAds.has(adUnitId)) {
    console.debug("%c%s is already injected", "color: white; background: indigo", adUnitId);
    return;
  }
  console.debug("%cRequesting ads", "color: white; background: indigo", adUnitId);
  c.injectNewAds(adUnitId, targetId);
  registeredAds.add(adUnitId);
}

/**
 * Ad context for KinoDK (concept.js ad system).
 *
 * Injection flow:
 *  1. On effect mount/re-run: if concept.js has already loaded (_conceptScriptLoaded),
 *     scan the DOM immediately for any registered elements and inject. Otherwise, wait
 *     for the "concept_loaded" event and inject then. _conceptScriptLoaded is module-level
 *     and survives client-side navigation, so the event only fires once per browser session.
 *
 *  2. Late-arriving elements (e.g. ads inside a client-fetched PosterGrid): the child
 *     KinodkAd component calls registerAdUnit(targetId) on mount. If concept.js is already
 *     loaded, injectForTarget() runs immediately for that slot.
 *
 *  3. Elements that leave and come back (e.g. user selects a different date): the child
 *     calls unregisterAdUnit(targetId) on cleanup, which removes the auIds from
 *     registeredAdUnits so the early-return guard in injectForTarget() doesn't block
 *     re-injection when the component remounts.
 *
 * Key invariant: registeredAdUnits tracks auIds (e.g. "cncpt-mob1"), NOT targetIds.
 * The concept system mutates element IDs after injection, so a subsequent getElementById
 * scan will return null for already-injected slots — this is expected and benign.
 */
function KinoDkAdWrapper(t0) {
  const $ = _c(30);
  const {
    children,
    ads,
    defaultProximity: t1
  } = t0;
  t1 === undefined ? 0 : t1;
  let t2;
  if ($[0] !== ads) {
    t2 = targetId => ads.find(ad => ad.targetId === targetId)?.auId ?? null;
    $[0] = ads;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const _getAuId = t2;
  const pathname = usePathname();
  const locationContext = useLocationContext();
  const cinemaContext = useSelectedCinemaContext();
  const [refreshTick, setRefreshTick] = useState(0);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const registeredAdUnits = useRef(t3);
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      console.debug("%cClearing all registered ad units", "color: white; background: indigo");
      registeredAdUnits.current.clear();
    };
    t5 = [];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] !== ads) {
    t6 = () => {
      if (getURLParameter("skipAds") === "true") {
        return;
      }
      const requestAds = () => {
        const c = window?.concept;
        if (!c) {
          console.warn("%cNo concept script loaded", "color: white; background: indigo");
          return;
        }
        _conceptScriptLoaded = true;
        console.debug("%cConfigured ads", "color: white; background: indigo", ads.flatMap(_temp2));
        ads.forEach(ad_0 => {
          const adUnitIds = ad_0?.auId?.split("|")?.filter(_temp3);
          adUnitIds?.forEach(adUnitId => {
            const elem = window?.document?.getElementById(adUnitId);
            if (!elem) {
              console.warn("%cNo element found with target %s", "color: white; background: indigo", adUnitId);
            } else {
              injectForTarget(registeredAdUnits.current, adUnitId, ad_0.targetId);
            }
          });
        });
      };
      const requestAdsDelayed = () => setTimeout(requestAds, 0);
      if (!_conceptScriptLoaded) {
        window?.document?.addEventListener("concept_loaded", requestAdsDelayed);
      } else {
        requestAds();
      }
      return () => {
        window?.document?.removeEventListener("concept_loaded", requestAdsDelayed);
        registeredAdUnits.current = new Set();
      };
    };
    $[5] = ads;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== ads || $[8] !== cinemaContext.isReady || $[9] !== cinemaContext.selectedCinemas || $[10] !== locationContext.isReady || $[11] !== locationContext.location || $[12] !== pathname || $[13] !== refreshTick) {
    t7 = [ads, locationContext.location, locationContext.isReady, cinemaContext.selectedCinemas, cinemaContext.isReady, pathname, refreshTick];
    $[7] = ads;
    $[8] = cinemaContext.isReady;
    $[9] = cinemaContext.selectedCinemas;
    $[10] = locationContext.isReady;
    $[11] = locationContext.location;
    $[12] = pathname;
    $[13] = refreshTick;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setRefreshTick(_temp4);
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const refresh = t8;
  let t9;
  if ($[16] !== ads) {
    t9 = targetId_0 => {
      if (!_conceptScriptLoaded) {
        return;
      }
      ads.filter(ad_1 => ad_1.targetId === targetId_0).forEach(ad_2 => {
        const adUnitIds_0 = ad_2?.auId?.split("|")?.filter(_temp5);
        adUnitIds_0?.forEach(adUnitId_0 => {
          injectForTarget(registeredAdUnits.current, adUnitId_0, ad_2.targetId);
        });
      });
    };
    $[16] = ads;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const _registerAdUnit = t9;
  let t10;
  if ($[18] !== ads) {
    t10 = targetId_1 => {
      console.debug("%cUnregistering", "color: white; background: indigo", targetId_1);
      ads.filter(ad_3 => ad_3.targetId === targetId_1).forEach(ad_4 => {
        ad_4?.auId?.split("|")?.filter(_temp6).forEach(adUnitId_1 => registeredAdUnits.current.delete(adUnitId_1));
      });
    };
    $[18] = ads;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const _unregisterAdUnit = t10;
  let t11;
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    t12 = [];
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  let t13;
  if ($[22] !== _getAuId || $[23] !== _registerAdUnit || $[24] !== _unregisterAdUnit) {
    t13 = {
      loadedAds: t11,
      noMatchedAds: t12,
      kv: undefined,
      setKv: _temp7,
      refresh,
      getAuId: _getAuId,
      registerAdUnit: _registerAdUnit,
      unregisterAdUnit: _unregisterAdUnit
    };
    $[22] = _getAuId;
    $[23] = _registerAdUnit;
    $[24] = _unregisterAdUnit;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Suspense fallback={null}><SearchParamsChangeListener onChange={refresh} /></Suspense>;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== children || $[28] !== t13) {
    t15 = <AdContext.Provider value={t13}>{children}{t14}</AdContext.Provider>;
    $[27] = children;
    $[28] = t13;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
}
//#endregion
function _temp7() {}
function _temp6(auId_1) {
  return auId_1?.trim() !== "";
}
function _temp5(auId_0) {
  return auId_0?.trim() !== "";
}
function _temp4(t) {
  return t + 1;
}
function _temp3(auId) {
  return auId?.trim() !== "";
}
function _temp2(a) {
  return a.auId?.split("|")?.filter(_temp);
}
function _temp(au) {
  return au?.trim() !== "";
}